import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteLayout } from "@/components/SiteLayout";
import { TollReportForm } from "@/components/TollReportForm";

const TITLE = "Contato — TrajetoFácil";
const DESCRIPTION =
  "Fale com a equipe do TrajetoFácil: dúvidas, correções de tarifas de pedágio, sugestões e parcerias pela página de contato.";
const URL = "https://www.trajetofacil.com.br/contato";

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <SiteLayout>
      <article className="space-y-8 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">Contato</h1>
          <p className="mt-3 text-muted-foreground">
            Encontrou uma tarifa divergente, tem uma sugestão de melhoria ou quer falar sobre parceria?
            Queremos ouvir você.
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Canal principal: feedback na ferramenta</h2>
          <p>
            A forma mais rápida de falar com a equipe é o botão <strong>Feedback</strong>, presente no
            cabeçalho de todas as páginas e dentro da calculadora. As mensagens caem direto no nosso painel
            interno e são lidas diariamente — inclusive correções de pedágio, que tratamos com prioridade.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">O que reportar</h2>
          <ul className="list-disc space-y-1.5 pl-6">
            <li>Tarifa de pedágio divergente do valor cobrado na praça (informe a rodovia e o sentido);</li>
            <li>Praça de pedágio que não apareceu na estimativa;</li>
            <li>Problema com endereço, CEP ou traçado de rota;</li>
            <li>Sugestões de novos recursos;</li>
            <li>Assuntos comerciais e parcerias.</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-bold">Reportar divergência de pedágio ou rota</h2>
          <p>
            Preencha os campos abaixo com o máximo de detalhe possível. Quanto mais precisa a informação
            (rodovia, sentido, categoria do veículo e valor cobrado), mais rápido conseguimos conferir na
            fonte oficial e corrigir a base.
          </p>
          <TollReportForm />
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">O que acontece com o seu reporte</h2>
          <ol className="list-decimal space-y-1.5 pl-6">
            <li>A mensagem entra no painel interno e é triada em dias úteis;</li>
            <li>Conferimos a tarifa ou a praça na fonte oficial (ANTT, regulador estadual ou concessionária);</li>
            <li>Confirmada a divergência, a base é corrigida e a alteração vale para todas as novas consultas;</li>
            <li>Casos recorrentes viram ajuste no processo automático de atualização semanal.</li>
          </ol>
          <p>
            Esse ciclo é o que mantém a base viva: boa parte das praças e tarifas revisadas nos últimos
            meses chegou por reportes de usuários. Detalhes do método estão na{" "}
            <Link to="/metodologia" className="font-medium text-primary underline underline-offset-4">
              página de metodologia
            </Link>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Prazo de resposta</h2>
          <p>
            Revisamos o painel de feedback todos os dias úteis. Correções de tarifas confirmadas entram na
            base na atualização seguinte. Por se tratar de uma ferramenta gratuita mantida por uma equipe
            pequena, nem todas as mensagens recebem resposta individual — mas todas são lidas.
          </p>
        </section>
      </article>
    </SiteLayout>
  );
}
